/*
 * tilerenderrig.ts - prepare and draw mesh tiles
 */

import MapMesh from './mesh';
import MapTexture from './texture';
import MapSurfaceTile from './surface-tile'
import MapBody from './body';
import Renderer from '../renderer/renderer';
import GpuProgram from '../renderer/gpu/program';
import GpuMesh from '../renderer/gpu/mesh';
import GpuTexture from '../renderer/gpu/texture';
import type * as viewerConfig from '../viewer-config';
import Atmosphere from './atmosphere';
import { grayPngDecodeAvailable } from '../utils/gray-png';
import type * as StyleSchema from './style-schema';
import RasterSource from './raster-source';
import type { LayerDesc } from
    '../renderer/tile-shader-specializer';

import * as illumination from './illumination';
import * as math from '../utils/math';
import {vec3} from '../utils/matrix';
import * as utils from '../utils/utils';


//import * as utils from '../utils/utils';


 /**
  * A tile render rig manages the render process for a specific terrain tile.
  * Regular terrain meshes contain exactly one submesh; any other count is
  * malformed input and produces no draw.
  *
  * It resolves and prepares the resources needed for rendering, checks their
  * readiness, and draws the tile with `MapMesh.draw2` after binding textures
  * and setting uniforms and samplers.
  *
  * The rig replaced the old terrain draw-command path. It renders each tile
  * in one pass with one program, including optional atmospheric scattering.
  *
  * The rig is self-contained in the sense that it can draw independently even
  * when/while the original tile changes.
  *
  * There are two levels of draw readiness: full readiness (or simply readiness)
  * and fallback readiness. The fallback readiness is meant for a tile that is
  * meant to be a replacement for better (typically higher resolution) data.
  * This matters: not all data are made ready for the sake of fallback
  * readiness.
  */

export class TileRenderRig {

    private readonly config!: Readonly<viewerConfig.ViewerConfig>;
    private readonly tile!: MapSurfaceTile;
    private readonly renderer!: Renderer;

    private mesh!: MapMesh;
    private submeshIndex!: number;

    private normalMap?: MapTexture;

    private uboLayers?: WebGLBuffer;

    // Backing store for the layers uniform block, filled fresh on every
    // draw. Preallocated once per rig so the draw path does not allocate
    // (and later garbage-collect) a buffer, two typed views and the
    // sampler array on every frame.
    private readonly uboBuf = new ArrayBuffer(UboLayersSize);
    private readonly uboF32 = new Float32Array(this.uboBuf); // for vec4
    private readonly uboI32 = new Int32Array(this.uboBuf);   // for ivec4
    private readonly uboSamplers = new Int32Array(MaxTextures);

    private collapsed: {
        normalGpu: GpuTexture;
        cacheItem: object;
        layerIndices: number[];
    } | null = null;

    private rt : {
        illumination: boolean,
        normals: boolean,
        //externalUVs: boolean,
        //internalUVs: boolean,
        layerStack: Layer[],
        upVector: math.vec3
    } = {

        illumination: false,
        normals: false,
        //externalUVs: false,
        //internalUVs: false,
        layerStack: [],
        upVector: [0, 0, 1]
    }

    /**
     * A new tile render rig.
     */

    constructor(tile: MapSurfaceTile, style: StyleSchema.StyleSpecification,
        renderer: Renderer, config: Readonly<viewerConfig.ViewerConfig>) {

        this.tile = tile;
        this.renderer = renderer;
        this.config = config;

        this.mesh = tile.surfaceMesh;
        this.submeshIndex = 0;

        // examine surface
        const surface = tile.resourceSurface;

        this.rt.illumination
            = !! surface.normalsUrl && this.renderer.getIlluminationState()

        this.rt.normals = !! surface.normalsUrl && ! config.mapNoNormalMaps;

        // build the layer stack - this may change the flags due to optimization
        this.buildLayerStack(style);

        // add the tile upVector to the runtime information
        // (passed on to the program)
        const nodeInfo = tile.map.measure.getNodeInformation(tile.id);

        if (!nodeInfo) {
            __DEV__ && console.warn(
                `${this.logSign()}: missing upVector in node information, `);
        } else {
            this.rt.upVector = nodeInfo.upVector;
        }
        //__DEV__ && console.log(
        //    `${this.logSign()}: upVector: ${this.rt.upVector}`);

        // done

    } // constructor

    /**
     * Make rig resources ready and check readiness.
     *
     * @param readiness the two levels of readiness requested.
     *     *Minimum* controls
     *     which resources suffice for the tile rig to be evaluated as ready.
     *     *Desired* controls which resources the method *attempts* to make
     *     ready (activates their readiness triggers).
     * @param priority the priority for essential and optional resources.
     * @return true if resources are ready on the desired rendering level
     */
    isReady(readiness: TileRenderRig.ReadinessLevels = {
                minimum : 'full', desired: 'full' },
            priority = TileRenderRig.DefaultPriority,
            options = TileRenderRig.DefaultIsReadyOptions): boolean {

        let layerStack = this.rt.layerStack;

        // if we have any 'notSureYet' masks, initiate checks and exit
        // gracefully
        let unsureMasks = false;

        layerStack.forEach((item: Layer) => {

            if (item.source !== 'texture') return;

            const texture = item.srcTextureTexture;

            item.rt.hasMask = TileRenderRig.hasMask(texture);

            if (item.rt && item.rt.hasMask === 'notSureYet') {

                //console.log(
                //    'Checking mask for %s (%s): ',
                //    this.tile.id.join('-'),
                //    item.rt.rasterSourceId);

                console.assert(
                    item.source === 'texture', 'incompatible layer params');

                texture.isReady(options.doNotLoad, priority.essential,
                    options.doNotCheckGpu);

                item.rt.hasMask = TileRenderRig.hasMask(texture);


                if (item.rt.hasMask === 'notSureYet') unsureMasks = true;
            }

            if (item.operation === 'blend') {

                item.rt.isWatertight =
                    item.rt.hasMask === 'no'
                    && item.opBlendAlpha.value === 1.0
                    && item.opBlendAlpha.mode === 'constant'
                    && item.opBlendMode === 'overlay'
                    && !item.rt.isTransparent;
            }
        });

        if (unsureMasks) {
            // console.log(`${this.logSign()}: unsure masks`);
            return false;
        }

        // optimize stack here
        this.optimizeStack();

        // actual readiness starts
        let ready_: boolean = true;

        // mesh is always essential
        ready_ &&= TileRenderRig.isResourceReady(this.mesh, 'essential',
            readiness, priority, options);

        // escape hatch for malformed mesh geometry
        if (ready_ && !this.hasGeometry()) return true;

        // process layerStack
        layerStack.forEach((item: Layer) => {

            if (item.rt.optimizedOut) return;

            // note the check is forced even when ready_ === false
            ready_ = this.isLayerReady(item, readiness, priority, options)
                && ready_;
        });

        /*if (ready_ && utils.compareTuples(this.tile.id, [15, 8772, 5758])) {
            console.log('%s render-ready.', this.logSign());
        }*/

        // if the rig is ready and uboLayers has not been created, create it
        if (ready_ && !this.uboLayers) this.createBuffer();

        // done
        return ready_;
    }

    /** Whether the loaded mesh contains drawable terrain geometry. */
    hasGeometry(): boolean {

        return this.mesh.submeshes.length === 1;
    }

    /**
     * Check rig for auxiliary depth rendering readiness.
     * @priority the priority for the mesh
     * @options options for the mesh readiness check
     */
    isDepthReady(priority = TileRenderRig.DefaultPriority.essential,
        options = TileRenderRig.DefaultIsReadyOptions): boolean {

        // only the mesh is needed for depth rendering
        return TileRenderRig.isResourceReady(this.mesh, 'essential',
            { minimum: 'full', desired: 'full' },
            {  ... TileRenderRig.DefaultPriority, essential: priority },
            options);
    }


    /**
     * Process layer stack into an actual draw call, using the tile shader
     * program.
     * @cameraPos camera position in world coordinates
     * @maskTexture optional UV-space coverage mask to discard fragments
     */
    draw(cameraPos: math.vec3, maskTexture?: GpuTexture) {

        if (!this.hasGeometry()) return;

        if (!this.uboLayers) {
            console.warn(
                `draw called on an unready rig for ${this.logSign()}.`);
            return;
        }

        // discard-free program for plain tiles; the discarding variant
        // keeps the coverage-mask discard, driven by the mask texture.
        const needsDiscard = !!maskTexture;

        // fill the UBO and collect layer descriptors; the descriptors
        // carry the sampler indices assigned during encoding, so the
        // program must be selected after this runs
        const layerDescs = this.fillLayerBuffer();

        // the specialized program for the layer-stack shape; an empty
        // stack specializes to a black-output program
        const program =
            this.renderer.programTileSpecialized(layerDescs, needsDiscard);

        /* make sure we got the right program (device caches the current program,
         * so no extra churn) */
        this.renderer.gpu.useProgram2(program);

        // sampler array uniform for the layer textures
        program.setIntArray('uTexture[0]', this.uboSamplers);

        // uModel
        program.setMat4('uModel', this.submesh.getWorldMatrix(cameraPos));

        // uUpVector
        program.setVec3('uUpVector', this.rt.upVector);

        // coverage uniforms exist only on the discarding program
        if (needsDiscard) this.bindMask(program, maskTexture);

        // draw
        let attrNames: GpuMesh.AttrNames = { position: 'aPosition' };
        if (this.mesh.hasInternalUVs) attrNames.uvs = 'aTexCoords';
        if (this.mesh.hasExternalUVs) attrNames.uvs2 = 'aTexCoords2';

        const gpuSubmesh = this.mesh.gpuSubmeshes[this.submeshIndex];
        gpuSubmesh.draw2(program, attrNames);

        //console.log(`this.logSign(): draw.`);
    }

    /**
     * Draw the tile into an auxiliary depth buffer.
     * @cameraPos camera position in world coordinates
     * @maskTexture optional UV-space coverage mask to discard fragments
     */
    drawDepth(cameraPos: math.vec3, maskTexture?: GpuTexture) {

        if (!this.hasGeometry()) return;

        const program = this.renderer.programTileDepth();
        this.renderer.gpu.useProgram2(program);

        // uModel
        program.setMat4('uModel', this.submesh.getWorldMatrix(cameraPos));

        // uMask
        this.bindMask(program, maskTexture);

        // draw
        let attrNames: GpuMesh.AttrNames = { position: 'aPosition' };
        if (this.mesh.hasExternalUVs) attrNames.uvs2 = 'aTexCoords2';

        const gpuSubmesh = this.mesh.gpuSubmeshes[this.submeshIndex];
        gpuSubmesh.draw2(program, attrNames);
    }

    /**
     * Draw the tile's terrain height into an elevation-store unit, in
     * tile UV space, so the draw covers the whole tile. The height
     * carries no vertical exaggeration.
     *
     * @param cameraPos camera position in world coordinates
     * @param heightRange the reference frame's declared height range,
     *     which orders overlapping triangles of this draw
     * @param geocentric geodetic height above the ellipsoid when true,
     *     physical Z when false
     * @param maskTexture optional UV-space coverage mask to discard
     *     fragments already covered
     * @return whether the tile drew; a caller that builds coverage from
     *     several tiles must treat false as an incomplete result
     */
    drawElevation(
        cameraPos: math.vec3,
        heightRange: [number, number],
        geocentric: boolean,
        maskTexture?: GpuTexture,
    ): boolean {

        if (!this.hasGeometry()) return false;

        if (!this.mesh.hasExternalUVs) {

            __DEV__ && utils.warnOnce(
                `${this.logSign()}: drawElevation() without external UVs.`);
            return false;
        }

        const program = this.renderer.programElevationRaster();
        this.renderer.gpu.useProgram2(program);

        program.setMat4('uModel', this.submesh.getWorldMatrix(cameraPos));
        program.setBool('uGeocentric', geocentric);
        program.setVec2('uHeightRange', heightRange);

        this.bindMask(program, maskTexture);

        const attrNames: GpuMesh.AttrNames = {
            position: 'aPosition',
            uvs2: 'aTexCoords2',
        };

        const gpuSubmesh = this.mesh.gpuSubmeshes[this.submeshIndex];
        gpuSubmesh.draw2(program, attrNames);
        return true;
    }

    /**
     * Render this tile's geographic footprint into the active mask target.
     */
    footprint() {

        if (!this.hasGeometry()) return;

        if (!this.mesh.hasExternalUVs) {

            __DEV__ && utils.warnOnce(
                `${this.logSign()}: footprint() without external UVs.`);
            return;
        }

        const program = this.renderer.programTileMaskFootprint();
        this.renderer.gpu.useProgram2(program);

        let attrNames: GpuMesh.AttrNames = {
            position: 'aPosition',
            uvs2: 'aTexCoords2'
        };

        const gpuSubmesh = this.mesh.gpuSubmeshes[this.submeshIndex];
        gpuSubmesh.draw2(program, attrNames);
    }


    private bindMask(program: GpuProgram, maskTexture?: GpuTexture) {

        const maskEnabled = !!maskTexture;
        program.setBool('uMaskEnabled', maskEnabled);

        if (!maskTexture) return;

        const unit = this.renderer.textureIdxs.tileMask;
        this.renderer.gpu.bindTexture(maskTexture, unit);
        program.setSampler('uMask', unit);
    }

    /**
     * create the layer UBO
     */
    private createBuffer() {

        let gl = this.renderer.gpu.gl;

        this.uboLayers = gl.createBuffer();

        gl.bindBuffer(gl.UNIFORM_BUFFER, this.uboLayers);

        gl.bufferData(gl.UNIFORM_BUFFER, UboLayersSize, gl.DYNAMIC_DRAW);
        gl.bindBuffer(gl.UNIFORM_BUFFER, null);
    }

    /**
     * Fill the layer UBO, bind textures and collect one descriptor per
     * encoded layer for the shader specializer. Returns the descriptors
     * so the caller can select the specialized program.
     */
    private fillLayerBuffer(): LayerDesc[] {

        let gl = this.renderer.gpu.gl;

        // bind buffer base
        gl.bindBufferBase(gl.UNIFORM_BUFFER,
            Renderer.UniformBlockName.Layers, this.uboLayers ?? null);

        // update hot alpha blending values, both static and dynamic
        this.updateAlphas();

        // reuse the preallocated backing buffer and its two typed views;
        // every active word is overwritten below, so no clearing is needed
        const bufacc = {
            f32: this.uboF32,
            i32: this.uboI32,
            woffset: 0                   // word offset
        }

        // reuse the preallocated samplers array; clear it so stale unit
        // indices from a previous draw are not uploaded to the program
        this.uboSamplers.fill(0);

        let samplers = {

            samplers: this.uboSamplers,
            nextIdx: 0,
            nextTextureUnit: FirstLayerTextureUnit,
            ub: FirstLayerTextureUnit + MaxTextures
        }

        // we encode the layers first: due to fallback rendering, we
        // do not know the count until we process all of them
        let numLayers = 0;
        bufacc.woffset = 4;

        // one descriptor per encoded layer, in draw order
        const descs: LayerDesc[] = [];

        this.rt.layerStack.forEach((layer) => {

            // sanity
            if (numLayers >= MaxLayers)
                throw Error('maximum rendering layers exhausted, aborting.');

            // skip optimized-out layer
            if (layer.rt.optimizedOut) return;

            // skip a layer with nothing to bind; an essential one is a
            // readiness fault upstream
            if (!this.isLayerResident(layer)) {

                if (layer.necessity === 'essential')
                    __DEV__ && utils.warnOnce(`${this.logSign()}: `
                        + 'Essential layer not resident, bad flow.');

                return;
            }

            // record the sampler slot the main texture will take
            const preIdx = samplers.nextIdx;

            // LayerRaw layers[16];
            this.encodeLayer(layer, bufacc, samplers)

            // build the descriptor matching what encodeLayer emitted;
            // flagMask is the raw integer the GLSL compares against
            const desc: LayerDesc = {
                target: layer.target,
                source: layer.source,
                operation: layer.operation,
                flagMask: layer.flagMask ?? Renderer.RenderFlags.FlagNone,
            };

            if (layer.source === 'shade')
                desc.srcShadeType = layer.srcShadeType;

            if (layer.source === 'texture') {

                desc.srcTextureUVs = layer.srcTextureUVs;
                desc.srcTextureSampling = layer.srcTextureSampling;
                desc.srcTextureIdx = preIdx;

                // the mask, if bound, takes the slot after the main
                // texture; encodeLayer binds main then mask
                const maskBound = samplers.nextIdx > preIdx + 1;

                desc.srcTextureMaskIdx = maskBound ? preIdx + 1 : -1;
            }

            if (layer.operation === 'blend')
                desc.opBlendMode = layer.opBlendMode;

            descs.push(desc);
            numLayers++;
        })

        // set the layer count last
        bufacc.i32.set([numLayers], 0);            // ivec4 layerCount

        //if (numLayers < this.rt.layerStack.length)
        //    __DEV__ && console.log(`${this.logSign()}: encoded `
        //        + `${numLayers} / ${this.rt.layerStack.length} layers.`);

        // update buffer
        gl.bindBuffer(gl.UNIFORM_BUFFER, this.uboLayers ?? null);
        gl.bufferSubData(gl.UNIFORM_BUFFER, 0, this.uboBuf);
        gl.bindBuffer(gl.UNIFORM_BUFFER, null);

        //__DEV__ && console.log(`${this.logSign()}: bound `
        //    + `${samplers.nextTextureUnit - FirstLayerTextureUnit}`
        //    + ` texture units.`);

        return descs;
    }

    private encodeLayer(layer: Layer,
        bufacc: { f32: Float32Array, i32: Int32Array, woffset: number },
        samplers: { samplers: Int32Array, nextIdx: number,
                    nextTextureUnit: number, ub: number }) {

        let renderer = this.renderer;

        const i32 = bufacc.i32, f32 = bufacc.f32;
        let w = bufacc.woffset;

        // tag.x, tag.y, tag.z
        i32[w++] = TargetMap[layer.target] ?? -1;
        i32[w++] = SourceMap[layer.source] ?? -1;
        i32[w++] = OpMap[layer.operation] ?? -1;


        // tag.w
        switch (layer.source) {

            case 'texture':
                i32[w++] = TexSamplingMap[layer.srcTextureSampling] ?? -1;
                break;

            default:
                w++;
        }

        // vec4 p0
        switch (layer.source) {

            case 'shade':

                i32[w++] = ShadeTypeMap[layer.srcShadeType] ?? -1;       // p0.x
                i32[w++] = ShadeNormalMap[layer.srcShadeNormal] ?? -1;   // p0.y
                i32[w++] = 0;                                            // p0.z
                break;

            case 'texture':
                const texture = layer.srcTextureTexture;
                let mainIdx = -1, maskIdx = -1;

                const main = this.mainGpuTexture(layer);
                let mask: ReturnType<MapTexture['getGpuMaskTexture']>;

                // the collapsed normal carries no mask; binding it keeps
                // it warm in the GPU cache
                if (this.collapsed && main === this.collapsed.normalGpu) {

                    this.tile.map.gpuCache.updateItem(
                        this.collapsed.cacheItem);
                    mask = null;

                } else {

                    mask = texture.getGpuMaskTexture();
                }

                const needUnits = (main ? 1 : 0) + (mask ? 1 : 0);
                if (samplers.nextTextureUnit + needUnits > samplers.ub)
                    throw Error('no more available texture units.');


                if (main) {

                    let mainUnit = samplers.nextTextureUnit++;

                    mainIdx = samplers.nextIdx++;
                    samplers.samplers[mainIdx] = mainUnit;

                    //console.log(
                    //    `${this.logSign()}: binding `
                    //    + `${layer.rt.rasterSourceId} main.`);
                    renderer.gpu.bindTexture(main, mainUnit);
                }

                if (mask) {

                    let maskUnit = samplers.nextTextureUnit++;

                    maskIdx = samplers.nextIdx++;
                    samplers.samplers[maskIdx] = maskUnit;

                    //console.log(
                    //    `${this.logSign()}: binding `
                    //    + `${layer.rt.rasterSourceId} mask.`);
                    renderer.gpu.bindTexture(mask, maskUnit);
                }

                i32[w++] = mainIdx;                                     // p0.x
                i32[w++] = maskIdx;                                     // p0.y
                i32[w++] = TexUVsMap[layer.srcTextureUVs] ?? -1;         // p0.z
                break;

            default:
                w += 3;                                          // p0.xyz
        }


        switch (layer.operation) {
            case 'blend':

                i32[w++] = BlendModeMap[layer.opBlendMode] ?? -1;       // p0.w
                break;


            default:
                w++;                                                     // p0.w
        }

        // vec4 p1
        switch (layer.source) {

            case 'constant':
                const c = layer.srcConstant;
                f32[w++] = c[0]; f32[w++] = c[1];
                f32[w++] = c[2];                                  // p1.xyz
                w++;                                              // p1.w
                break;

            case 'texture':
                const t = layer.srcTextureTexture.getTransform();
                f32[w++] = t[0]; f32[w++] = t[1];                  // p1.xy
                f32[w++] = t[2]; f32[w++] = t[3];                  // p1.zw
                break;

            default:
                w += 4;                                           // p1.xyzw
        }

        // vec4 p2
        switch (layer.operation) {

            case 'blend':

                f32[w++] = layer.rt.alpha ?? 0;                    // p2.x
                break;

            default:
                w++;                                              // p2.x
        }

        switch (layer.target) {

            case 'color':
                f32[w++] = layer.tgtColorWhitewash ?? 0;           // p2.y
                break;

            default:
                w++;                                                     // p2.y
        }

        w += 2;                                                 // p2.zw

        // ivec4 p3
        const fm = Renderer.encodeRenderFlags(
            layer.flagMask ?? Renderer.RenderFlags.FlagNone);
        i32[w++] = fm[0];
        i32[w++] = fm[1];                               // p3.xy — flag mask
        i32[w++] = fm[2]; i32[w++] = fm[3];                    // p3.zw reserved

        // done
        bufacc.woffset = w;

        //__DEV__ && console.log(
        //    `${this.logSign()}: uboLayers offset: ${bufacc.woffset * 4}`);
    }

    /**
     *  Update the alpha values for rendering - a suboptimial implementation
     */
    updateAlphas() {

        let renderer = this.renderer;

        // normalize view-dependent alphas for raster layers
        let vdalphaSum = 0;
        const epsilon = 1E-3;

        // pass 1
        this.rt.layerStack.forEach((layer) => {

            if (layer.operation !== 'blend') return;

            const alpha = layer.opBlendAlpha;

            if (alpha.mode === 'constant') {
                layer.rt.alpha = alpha.value;
                return;
            }

            // FIXME doing this for every frame and every tile is suboptimal,
            // the alphas may be stored in renderer object and not updated
            // until the the map updates
            layer.rt.alpha = math.clamp(
                    Math.pow(vec3.dot(
                        alpha.illuminationNED,
                        renderer.getIlluminationVectorNED()), 5),
                    0.0, 1.0) + epsilon;

            vdalphaSum += layer.rt.alpha;
        });

        //console.log(vdalphaSum);

        if ( vdalphaSum === 0) return;
        // pass 2: normalization (and application of original alpha as
        // multiplier)
        this.rt.layerStack.forEach((layer) => {

            if (layer.operation !== 'blend'
                || layer.opBlendAlpha.mode !== 'viewdep') return;

            const alpha = layer.opBlendAlpha;
            layer.rt.alpha = (layer.rt.alpha ?? 0) * alpha.value / vdalphaSum;
        });

    }

    /*
     * delete the external gpu resources
     */
    dispose() {

        //__DEV__ && console.log(
        //    `${this.logSign()}: disposing of UBO.`);

        const collapsed = this.collapsed;
        if (collapsed) this.tile.map.gpuCache.remove(collapsed.cacheItem);

        let gl = this.renderer.gpu.gl;

        gl.deleteBuffer(this.uboLayers ?? null);
    }

    /**
     * Retrieves the raster source ids whose texture this tile draws.
     * Used to assemble imagery credits.
     */

    activeRasterSourceIds(): string[] {

        let ret = [] as string[];

        this.rt.layerStack.forEach((item: Layer) => {

            if (this.isLayerResident(item) && item.rt.rasterSourceId)
                ret.push(item.rt.rasterSourceId);
        });

        //console.log(this.logSign(), ret);

        return ret;
    }


    private buildLayerStack(style: StyleSchema.StyleSpecification) {

        // build the stack
        const rt = this.rt;
        const tile = this.tile;

        // first scan the style, and obtain bump, diffuse and specular layers
        // pertaining to the active surface
        let bumps: StyleSchema.BumpMapLayer[] = [];
        let diffuses: (StyleSchema.DiffuseLayer) [] = [];
        let speculars: StyleSchema.SpecularMapLayer[] =  []

        style.layers && style.layers.forEach(
            (item: StyleSchema.LayerSpecification) => {

            let type_ = item.type ?? 'diffuse-map';

            if (!['bump-map', 'diffuse-map', 'constant', 'diffuse-constant',
                'specular-map'].includes(type_)) return;

            let item_ = item as StyleSchema.TileLayer;

            const terrainSourceId = this.tile.resourceSurface.id;
            let pertinent = !item_.terrain
                || item_.terrain.includes(terrainSourceId);
            if (!pertinent) return;

            if (type_ === 'bump-map')
                bumps.push(item_ as StyleSchema.BumpMapLayer);

            if (type_ === 'specular-map')
                speculars.push(item_ as StyleSchema.SpecularMapLayer);

            if (['diffuse-map', 'diffuse-constant', 'constant'].includes(type_))
                diffuses.push(item_ as StyleSchema.DiffuseLayer);
        });

        // target 'normal' layers need to come first, so that updated normal
        // is used for target 'color'

        // add normal map
        if (this.rt.illumination) {

            if (this.rt.normals) {

                // use normal maps
                let path = tile.resourceSurface.getNormalsUrl(
                    tile.id, this.submeshIndex);

                const normalMap = tile.resources.getTexture(
                    path, GpuTexture.Type.NormalMap, null, null, tile, true);
                this.normalMap = normalMap;

                rt.layerStack.push({
                    target: 'normal',
                    source: 'texture',
                    srcTextureTexture: normalMap,
                    srcTextureUVs: 'external',
                    srcTextureSampling: 'normal',
                    operation: 'push',
                    necessity: 'essential',
                    flagMask: Renderer.RenderFlags.FlagNormalMaps,
                    rt: {}
                });

            }

            // the shader declares the normal stack with flatNormal at its
            // base, so bump layers always have a valid normal to blend
            // into.
        }

        // add bump layers, if any
        bumps.forEach((item) => {

            let necessity = item.necessity ?? 'optional';

            let layer = this.layerFromDef(item, necessity, false, 'normal',
                    Renderer.RenderFlags.FlagBumpMaps);

            if (layer) rt.layerStack.push(layer);

        });

        // target 'color'

        // push the body's surface color as background
        rt.layerStack.push({
            target: 'color',
            source: 'constant',
            operation: 'push',
            necessity: 'essential',
            srcConstant: tile.map.referenceFrame?.body?.surfaceColor
                ?? MapBody.DefaultSurfaceColor,
            tgtColorWhitewash: 0,
            rt: {},
        });

        // if internal textures exist, overlay an internal texture
        if (tile.resourceSurface.textureUrl) {

            let path = tile.resourceSurface.getTextureUrl(
                tile.id, this.submeshIndex);
            let internalTexture = tile.resources.getTexture(
                path, GpuTexture.Type.Color, null, null, tile, true);

            rt.layerStack.push({
                target: 'color',
                source: 'texture',
                operation: 'blend',
                necessity: 'essential',
                srcTextureTexture: internalTexture,
                srcTextureUVs: 'internal',
                srcTextureSampling: 'raw',
                opBlendMode: 'overlay',
                opBlendAlpha: { mode: 'constant', value: 1.0 },
                tgtColorWhitewash: 0,
                rt: {}
            });

        }

        diffuses.forEach((item) => {

            let necessity = item.necessity ?? 'essential';

            let layer = this.layerFromDef(item, necessity,
                true, 'color', Renderer.RenderFlags.FlagDiffuseMaps);
            if (layer) rt.layerStack.push(layer);
        });

        // if there is illumination, blend-multiply by a diffuse shading layer
        if (rt.illumination) {

            rt.layerStack.push({
                target: 'color',
                source: 'shade',
                operation: 'blend',
                necessity: 'essential',
                srcShadeType: 'diffuse',
                srcShadeNormal: this.rt.normals ? 'normal-map' : 'flat',
                opBlendMode: 'multiply',
                opBlendAlpha: { mode: 'constant', value: 1.0 },
                tgtColorWhitewash: 0,
                flagMask: Renderer.RenderFlags.FlagLighting,
                rt: {}
            });
        }

        // add specular raster layers (if illuminated)
        if (rt.illumination && speculars.length > 0) {

            const specularMask = Renderer.RenderFlags.FlagLighting
                | Renderer.RenderFlags.FlagSpecularMaps;

            // push black as background
            rt.layerStack.push({
                target: 'color',
                source: 'constant',
                operation: 'push',
                necessity: 'essential',  // sanity
                srcConstant: [0, 0, 0],
                tgtColorWhitewash: 0,
                flagMask: specularMask,
                rt: {}
            });

            // process specular layers
            speculars.forEach((item) => {

                let necessity = item.necessity ?? 'essential';

                let layer = this.layerFromDef(item, necessity,
                    true, 'color', specularMask);
                if (layer) rt.layerStack.push(layer);

            });

            // specular shade with specular multiply
            rt.layerStack.push({
                target: 'color',
                source: 'shade',
                operation: 'blend',
                necessity: 'essential', // sanity
                srcShadeType: 'specular',
                srcShadeNormal: this.rt.normals ? 'normal-map' : 'flat',
                opBlendMode: 'specular-multiply',
                opBlendAlpha: { mode: 'constant', value: 1.0 },
                tgtColorWhitewash: 0,
                flagMask: specularMask,
                rt: {}
            });

            // pop-add, to add to the underlying color
            rt.layerStack.push({
                target: 'color',
                source: 'pop',
                operation: 'blend',
                necessity: 'essential', // sanity
                opBlendMode: 'add',
                opBlendAlpha: { mode: 'constant', value: 1.0 },
                tgtColorWhitewash: 0,
                flagMask: specularMask,
                rt: {}
            });

        } // if (rt.illumination && speculars.length > 0)

        // add atmosphere
        //
        // iOS color-manages decoded PNG pixels, which corrupts the
        // density texture encoding (it rendered as concentric rings).
        // The gray-png decoder returns the stored bytes verbatim;
        // suppress the haze layer only where that decoder cannot run
        // (iOS before 16.4).
        const atmosphereSupported = !utils.isIos()
            || grayPngDecodeAvailable();

        if (atmosphereSupported && tile.map.atmosphere) {

            rt.layerStack.push({
                target: 'color',
                source: 'atm-density',
                necessity: 'optional',
                operation: 'atm-color',
                tgtColorWhitewash: 0,
                flagMask: Renderer.RenderFlags.FlagAtmosphere,
                rt: {}
            });
        }

        // add shadows
        if (style.shadows) {

            rt.layerStack.push({
                target: 'color',
                source: 'none',
                necessity: 'optional',
                operation: 'shadows',
                tgtColorWhitewash: 0,
                flagMask: Renderer.RenderFlags.FlagShadows,
                rt: {}
            });
        };

        // done
        //__DEV__ && console.log(
        //    '%s (%s):',
        //    this.tile.id.join('-'),
        //    tile.resourceSurface.id,
        //    this.rt.layerStack);
    }


    private static hasMask(texture: MapTexture): MaskStatus {

        if (texture.isMaskPossible()) {

            if (texture.isMaskInfoReady()) {

                if (texture.getMaskTexture()) return 'yes';

                // originally, getMaskTexture() was not reliable
                // source of information on (later) existence of mask
                // seems fine now with the experimental fix in texture.js
                return 'no';
            }

            return 'notSureYet';
        }

        return 'no';
    }


    /**
     * rudimentary layer stack optimization
     */
    optimizeStack() {

        // watertight optimization
        // start of accumulation
        let cleanSlate = 0;

        for (let i = 0; i < this.rt.layerStack.length; i++) {

            let layer = this.rt.layerStack[i];

            // if there is a tile which always fully covers underlying tiles
            if (layer.target == 'color' && layer.rt.isWatertight) {

                // optimize out all tiles from teh start of accumulation
                for (let k = cleanSlate; k < i; k++)
                    if (this.rt.layerStack[k].target == 'color')
                        this.rt.layerStack[k].rt.optimizedOut = true;

                cleanSlate = i;
            }

            // push -> new accumulation
            if (layer.operation == 'push') cleanSlate = i + 1;

            // we could stack the slates on stack, optimize after pop,
            // but stacks are never this complicated
        }

        // bump-layer collapse
        if (this.config.mapCollapseBumps !== false) this.collapseNormalStack();
    }

    /**
     * Bake ready bump layers into a rig-local collapsed normal texture,
     * incrementally as each bump GPU texture becomes resident.
     */
    private collapseNormalStack() {

        const stack = this.rt.layerStack;

        // find the base normal-map push layer
        const pushIdx = stack.findIndex(
            l => l.target === 'normal' && l.operation === 'push');
        if (pushIdx < 0) return;

        // find first un-collapsed bump after the push layer
        let firstBump = -1;

        for (let i = pushIdx + 1; i < stack.length; i++) {
            const l = stack[i];
            if (l.target === 'normal'
                    && (l.operation === 'push' || l.source === 'pop')) break;
            if (l.target === 'normal' && l.source === 'texture'
                    && l.operation === 'blend' && !l.rt.optimizedOut) {
                firstBump = i; break;
            }
        }

        if (firstBump < 0) return;

        // prepare blend source; warm cache item to protect it before any
        // gpuCache operations below that could evict it
        const gpuCache = this.tile.map.gpuCache;

        if (!this.collapsed && !this.normalMap?.getGpuTexture()) return;
        if (this.collapsed) gpuCache.updateItem(this.collapsed.cacheItem);

        // check first bump GPU texture ready (read-only — no upload)
        const firstBumpLayer = stack[firstBump] as TextureLayer;
        if (!firstBumpLayer.srcTextureTexture.getGpuTexture()) return;

        // extract blend source handle
        const srcHandle = this.collapsed
            ? this.collapsed.normalGpu.texture
            : this.normalMap!.getGpuTexture()!.texture;
        if (!srcHandle) return;

        // blend sequence: init → base → each ready bump
        const nmb = this.renderer.nmblender;
        nmb.init('oct-normal');
        nmb.blend(srcHandle, 1.0);

        const collectedIndices: number[] = [];

        for (let i = firstBump; i < stack.length; i++) {

            const layer = stack[i];
            if (layer.target === 'normal' && (layer.operation === 'push'
                    || layer.source === 'pop')) break;
            if (layer.target !== 'normal' || layer.source !== 'texture'
                    || layer.operation !== 'blend'
                    || layer.rt.optimizedOut) continue;

            const bumpGpu = layer.srcTextureTexture.getGpuTexture();
            if (!bumpGpu) break;
            const bumpHandle = bumpGpu.texture;
            if (!bumpHandle) break;

            const alpha = layer.opBlendAlpha.mode === 'constant'
                ? layer.opBlendAlpha.value
                : (layer.rt.alpha ?? 0);
            nmb.blend(bumpHandle, alpha);
            collectedIndices.push(i);
        }

        if (!collectedIndices.length) return;

        // allocate collapsed texture on first collapse
        let normalGpu: GpuTexture;

        if (this.collapsed) normalGpu = this.collapsed.normalGpu;
        else {
            normalGpu = new GpuTexture(
                this.renderer.gpu, null, this.renderer.map);
            normalGpu.createFromData(
                256, 256, new Uint8Array(256 * 256 * 4),
                GpuTexture.Type.Color, 'linear');
        }

        // copy FBO result into the collapsed texture
        const dstHandle = normalGpu.texture;
        if (!dstHandle) return;
        nmb.copyResult(dstHandle);

        // register with cache or update existing registration
        if (!this.collapsed) {
            // populate layerIndices before insert() so the eviction
            // destructor can restore layers if immediately evicted
            this.collapsed = {
                normalGpu,
                cacheItem: null!,
                layerIndices: collectedIndices,
            };
            const cacheItem = gpuCache.insert(
                this.evictCollapsed.bind(this), normalGpu.getSize());
            if (!this.collapsed) return; // immediately evicted
            this.collapsed.cacheItem = cacheItem;
        } else {
            this.collapsed.layerIndices.push(...collectedIndices);
            gpuCache.updateItem(this.collapsed.cacheItem);
        }

        // mark collapsed layers optimized out only after cache registration
        for (const idx of collectedIndices)
            stack[idx].rt.optimizedOut = true;
    }

    /** Cache eviction destructor for the collapsed normal texture. */
    private evictCollapsed() {

        const collapsed = this.collapsed!;
        const gl = this.renderer.gpu.gl;

        collapsed.normalGpu[Symbol.dispose]();

        for (const idx of collapsed.layerIndices)
            this.rt.layerStack[idx].rt.optimizedOut = false;

        // delete the UBO so isReady() re-runs optimizeStack on next frame
        gl.deleteBuffer(this.uboLayers ?? null);
        this.uboLayers = undefined;

        this.collapsed = null;
    }

    /**
     * Create an internal layer operation (blend) with texture source from
     * a layer definition.
     *
     * @param layerDef layerDefinition
     * @param necessity is layer necessary or optional for rendering
     * @param propagate: should layer tiles propage beyeond their lodRange to
     *      higher lods? Usually true.
     * @param target 'color' (for the fragment color stack) or 'normal'
     *      (for the surface normal stack), normally 'color'.
     * @param flagMask render flag bits that must all be set in the frame
     *      renderFlags
     *      for the layer to execute; use FlagNone (0) to always execute.
     * @return resultant layer operation, or undefined if layer def yields none.
     */

    private layerFromDef(
        layerSpec: StyleSchema.TileLayer,
        necessity: Necessity, propagate: boolean,
        target: 'color' | 'normal',
        flagMask: Renderer.RenderFlags): Layer | undefined {

        let type_ = layerSpec.type ?? 'diffuse-map';

        if (['diffuse-map', 'bump-map', 'specular-map'].includes(type_)) {

            return this.textureLayerFromDef(
                layerSpec as StyleSchema.TileTextureLayer, necessity, propagate,
                target, flagMask);
        }

        if (type_ === 'constant' || type_ === 'diffuse-constant')
            return this.constantLayerFromDef(
                layerSpec as StyleSchema.DiffuseConstantLayer,
                necessity, propagate, target, flagMask);
    }

    private textureLayerFromDef(layerSpec: StyleSchema.TileTextureLayer,
                         necessity: Necessity,
                         propagate: boolean,
                         target: 'color' | 'normal',
                         flagMask: Renderer.RenderFlags): Layer | undefined {


        let tile = this.tile;
        const source =
            tile.map.outerMap.getRasterSource(layerSpec.source);

        let clampToLodRange = (tile: MapSurfaceTile, lodRange: number[]) => {

            while (tile.parent && tile.id[0] > lodRange[1]) {
                tile = tile.parent;
            }
            return tile;
        }

        const tileMatch = source.matchTile(tile.id);
        if (tileMatch === RasterSource.TileMatch.None) return undefined;

        let ancestorFallback = null;

        if (propagate
            && tileMatch === RasterSource.TileMatch.Ancestor) {

                // normally, we want to fallback to higher lod tiles if
                // tiles are not available on the requested lod. This provides
                // necessary information to MapTexture. In some cases, like
                // bump maps, this is not desirable.
                ancestorFallback = {
                    sourceTile: clampToLodRange(tile, source.lodRange),
                    sourceTexture: null, source, tile };

        }

        let texture: MapTexture = tile.rasterTextures[source.id];

        if (!texture) {

                let path = source.getUrl(tile.id);

                // a specular map is consumed through its red channel
                // only, so it is stored single-channel
                const textureType = layerSpec.type === 'specular-map'
                    ? GpuTexture.Type.SpecularMap : GpuTexture.Type.Color;

                texture = tile.resources.getTexture(
                    path, textureType, ancestorFallback,
                    {tile, source}, tile, false);

                if (source.coverage) {
                    texture.ancestorMaskPending = true;
                }

                texture.isReady(true); //check metatile/mask but do not load
                tile.rasterTextures[source.id] = texture;
        }

        if (texture.neverReady) return;

        // moved to readiness check
        //let hasMask = TileRenderRig.hasMask(texture);

        let alpha: Alpha, mode: BlendMode;
        [mode, alpha] = TileRenderRig.blendInfoFromDef(layerSpec);

        // moved to readiness check
        /*let isWatertight = !(hasMask != 'no' || alpha.value < 1.0
                || alpha.mode != 'constant' || mode != 'overlay'
                || source.isTransparent);*/


        let whitewash = layerSpec.whitewash ?? 0.0;

        // not a pretty side effect, copied verbatim from old code.
        // needed for credits extraction
        this.tile.rasterSources[source.id] = source;


        return ({
            operation: 'blend',
            source: 'texture',
            target: target,

            necessity: layerSpec.necessity ?? necessity,

            srcTextureTexture: texture,
            srcTextureUVs: 'external',
            srcTextureSampling: target == 'normal' ? 'normal' : 'raw',

            opBlendMode: mode,
            opBlendAlpha: alpha,

            tgtColorWhitewash: whitewash,
            flagMask: flagMask,

            rt: {
                rasterSourceId: source.id,
                //isWatertight: isWatertight,
                //hasMask: hasMask,
                isTransparent: source.isTransparent
            }
        });

    } // textureLayerFromDef

    private constantLayerFromDef(layerSpec: StyleSchema.DiffuseConstantLayer,
                         necessity: Necessity,
                         propagate: boolean,
                         target: 'color' | 'normal',
                         flagMask: Renderer.RenderFlags): Layer | undefined {

        let alpha: Alpha, mode: BlendMode;
        [mode, alpha] = TileRenderRig.blendInfoFromDef(layerSpec);

        let isWatertight = !(alpha.value < 1.0
            || alpha.mode != 'constant' || mode != 'overlay');

        let srcConstant = layerSpec.source.slice() as StyleSchema.Color3Spec;

        for (let i = 0; i < 3 ; i++) srcConstant[i] /= 255.;

        return {

            target: 'color',
            source: 'constant',
            operation: 'blend',
            opBlendMode: mode,
            opBlendAlpha: alpha,
            necessity: layerSpec.necessity ?? necessity,
            srcConstant: srcConstant,
            tgtColorWhitewash: 0,
            flagMask: flagMask,
            rt: { isWatertight: isWatertight }

        }
    }

    private static blendInfoFromDef(
        layerSpec: StyleSchema.TileLayer): [BlendMode, Alpha] {

        let mode: BlendMode = layerSpec.blendMode ?? 'overlay';

        const alphaSpec = layerSpec.alpha ?? 1.0;

        if (typeof alphaSpec === 'number') {
            return [mode, { mode: 'constant', value: alphaSpec }];
        }

        if (alphaSpec.mode === 'viewdep') {

            const illuminationAngles = alphaSpec.illumination;

            if (!illuminationAngles) {
                throw new Error(
                    'view-dependent alpha requires illumination angles.');
            }

            return [mode, {
                mode: 'viewdep',
                value: alphaSpec.value,
                illuminationNED: illumination.illuminationVector(
                    illuminationAngles[0],
                    illuminationAngles[1],
                    illumination.CoordSystem.NED)
            }];
        }

        return [mode, { mode: 'constant', value: alphaSpec.value }];
    }

    /**
     * Check if a given resource satifies the readiness condition, given the
     * necessity of the resource and the requested readiness levels.
     *
     * The side effect is making the resource ready if input values imply it
     * should be used for rendering.
     */
    private static isResourceReady(
        resource: MapMesh | MapTexture | Atmosphere,
        necessity: Necessity,
        readiness: TileRenderRig.ReadinessLevels,
        priority: TileRenderRig.Priority,
        options: TileRenderRig.IsReadyOptions) : boolean {


        let priority_: number =  priority[necessity];

        // coalasce desired >= minimum
        let minimum = readiness.minimum;
        let desired = readiness.minimum === 'fallback'
            ? readiness.desired : 'full';

        if (necessity === 'optional') {

            // optional resource in purely fallback rendering, ok
            if (desired === 'fallback')  return true;

            // optional resource, desired but not necessary
            if (minimum === 'fallback') {
                resource.isReady(options.doNotLoad, priority_,
                                 options.doNotCheckGpu);
                return true;
            }
        }

        // essential resource, or full readiness level requested
        return resource.isReady(options.doNotLoad,
                                 priority_, options.doNotCheckGpu);
    }

    private isLayerReady(layer: Layer,
        readiness: TileRenderRig.ReadinessLevels,
        priority: TileRenderRig.Priority,
        options: TileRenderRig.IsReadyOptions) : boolean {

        let necessity = layer.necessity;

        switch (layer.source) {

            case 'shade':
                if (this.rt.normals) {
                    if (!this.normalMap) return false;
                    return TileRenderRig.isResourceReady(this.normalMap,
                        necessity, readiness, priority, options);
                }
                break;

            case 'texture':
                return TileRenderRig.isResourceReady(layer.srcTextureTexture,
                        necessity, readiness, priority, options);

            case 'atm-density':
                if (!this.tile.map.atmosphere) return false;

                return TileRenderRig.isResourceReady(
                        this.tile.map.atmosphere,
                        necessity, readiness, priority, options);
        }

        return true;
    }

    /**
     * The GPU texture a texture layer samples: the collapsed normal for
     * the base normal-map push layer while a collapse is active, the
     * layer's own texture otherwise. Null when not resident.
     */
    private mainGpuTexture(layer: TextureLayer) {

        if (this.collapsed && layer.target === 'normal'
                && layer.operation === 'push')
            return this.collapsed.normalGpu;

        return layer.srcTextureTexture.getGpuTexture();
    }

    /**
     * Whether the layer's GPU resources can be bound right now. A
     * read-only test; loading and upload are `isReady()`'s business.
     */
    private isLayerResident(layer: Layer): boolean {

        switch (layer.source) {

            case 'texture':
                return !!this.mainGpuTexture(layer);

            case 'atm-density':
                return !!this.tile.map.atmosphere?.isResident();
        }

        return true;
    }

    private get submesh() { return this.mesh.submeshes[this.submeshIndex]; }

    /*
     * helper for diagnostics
     */
    private logSign(): string {

        return utils.idToString([...this.tile.id, this.submeshIndex]);
    }

}; // class TileRenderRig


// local types


type Necessity = 'essential' | 'optional';

type AlphaConstant = {
    mode: 'constant',
    value: number,
}

type AlphaViewDependent = {
    mode: 'viewdep',
    value: number,
    illuminationNED: math.vec3,
}

type Alpha = AlphaConstant | AlphaViewDependent;

type BlendMode = 'overlay' | 'add' | 'multiply' | 'specular-multiply';

type MaskStatus = 'yes' | 'no' | 'notSureYet';

/**
 * Layer stack item, basically an elementary instruction for the fragment
 * shader.
 */

type LayerRuntime = {

    rasterSourceId?: string,
    hasMask?: MaskStatus,
    isTransparent?: boolean,
    isWatertight?: boolean,
    optimizedOut?: boolean,
    alpha?: number,
}

type Layer = {

    target: 'color' | 'normal',
    necessity: Necessity;

    tgtColorWhitewash?: number,
    flagMask?: Renderer.RenderFlags,
    rt: LayerRuntime,

// what the source contributes: the operand value
} & (
    | { source: 'constant',
        srcConstant: [number, number, number] }

    | { source: 'shade',
        srcShadeType: 'diffuse' | 'specular',
        srcShadeNormal: 'normal-map' | 'flat' }

    | { source: 'texture',
        srcTextureTexture: MapTexture,
        srcTextureUVs: 'internal' | 'external',
        srcTextureSampling: 'raw' | 'normal' }

    | { source: 'pop' }
    | { source: 'atm-density' }
    | { source: 'none' }

// what the operation does with the operand
) & (
    | { operation: 'blend',
        opBlendMode: BlendMode,
        opBlendAlpha: Alpha }

    | { operation: 'push' }
    | { operation: 'atm-color' }
    | { operation: 'shadows' }
);

// a layer already known to sample a texture
type TextureLayer = Layer & { source: 'texture' };

// if you change this, change the corresponding literals in layers.inc.glsl
const NormalMapTextureIdx = 0;
const MaxLayers = 16;
const MaxTextures = 12;
const FirstLayerTextureUnit = NormalMapTextureIdx + 1;

// 1x ivec4 + MaxLayers * (2x ivec4 + 2x vec4 + 1x ivec4)
const UboLayersSize = 16 + MaxLayers * 80;


enum UboTarget {

    Color              = 0,
    Normal             = 1
}


enum UboSource {

    Constant           = 1,
    Texture            = 0,
    Pop                = 2,
    Shade              = 3,
    AtmDensity         = 4,
    Shadows            = 5,
    None               = 6
}


enum UboOperation {

    Blend           = 0,
    Push            = 1,
    AtmColor        = 2,
    Shadows         = 3
}

enum UboShadeType {

    Diffuse         = 0,
    Specular        = 1
}

enum UboShadeNormal {

    NormalMap     = 0,
    Flat          = 1
}

enum UboBlendMode {

    Overlay             = 0,
    Add                 = 1,
    Multiply            = 2,
    SpecularMultiply    = 3
}


enum UboTextureUVs {

    External           = 0,
    Internal           = 1
}

enum UboTextureSampling {

    Raw                 = 0,
    Normal              = 1
}


// encoding helper maps
const TargetMap = { color: UboTarget.Color, normal: UboTarget.Normal } as const;

const SourceMap = { constant: UboSource.Constant, shade: UboSource.Shade,
    pop: UboSource.Pop, 'atm-density': UboSource.AtmDensity,
    texture: UboSource.Texture, none: UboSource.None } as const;

const OpMap = { blend: UboOperation.Blend, push: UboOperation.Push,
    'atm-color': UboOperation.AtmColor,
    shadows: UboOperation.Shadows } as const;

const ShadeTypeMap = { diffuse: UboShadeType.Diffuse,
    specular: UboShadeType.Specular } as const;

const ShadeNormalMap = { 'normal-map': UboShadeNormal.NormalMap,
    flat: UboShadeNormal.Flat } as const;

const TexUVsMap = { internal: UboTextureUVs.Internal,
    external: UboTextureUVs.External } as const;

const TexSamplingMap = { raw: UboTextureSampling.Raw,
    normal: UboTextureSampling.Normal } as const;

const BlendModeMap = { overlay: UboBlendMode.Overlay, add: UboBlendMode.Add,
    multiply: UboBlendMode.Multiply,
    'specular-multiply': UboBlendMode.SpecularMultiply } as const;

// export types
export namespace TileRenderRig {

    /**
     * resource readiness level, see TileRenderRig.isReady for details
     */
    export type ReadinessLevel = 'fallback' | 'full';

    /**
     * the levels of readiness passed to isReady checks
     */
    export type ReadinessLevels = {
        minimum: ReadinessLevel,
        desired: ReadinessLevel
    }

    export const ReadinessFull: ReadinessLevels = {
        minimum: 'fallback', desired: 'full'
    };

    export const ReadinessFallback: ReadinessLevels = {
        minimum: 'fallback', desired: 'fallback'
    };

    /**
     * basic resources are necessary to render tile, extras are embelishments.
     */
    export const DefaultPriority = { essential: 0, optional: 0}

    export type Priority = typeof DefaultPriority;

    /**
     * These are passed to MapMesh.isReady() and MapTexture.isReady().
     *
     * The first one checks readiness without queueing requests for missing
     * content.
     * The second one seems to prevent checking agains exhaustion of gpu
     * resources.
     */
    export const DefaultIsReadyOptions = {
        doNotLoad: false, doNotCheckGpu: false
    }

    export type IsReadyOptions = typeof DefaultIsReadyOptions;
}
